import { CommonModule } from '@angular/common';
import { Component, input, output } from '@angular/core';
import { LegacyShimsModule } from './legacy-shims.module';

/**
 * Native-Angular port of the AngularJS `ftCampaignList` element
 * (account-v1/.../components/campaigns/directives/campaignListDirective.{js,html}).
 *
 * Renders the list of campaign panels (engagement / visitor stats, matched-criteria
 * summary, advocate/warm-lead goals, drag-reorder buttons in `sortMode`).
 *
 * IMPORTANT — every v1 filter is computed INLINE as a method here rather than as
 * a sibling shim pipe. Declaring new pipes in LegacyShimsModule that import
 * `app.module` (for `$translate`/`$injector`) perturbs the account-shared barrel
 * eval order and crashes the whole SPA at runtime ("Cannot read properties of
 * undefined") — the AOT build still succeeds, so it's only caught by a runtime
 * load. See [[project_account_barrel_eval_order_fragility]]. This component adds
 * NO declarations/imports to LegacyShimsModule itself. The only template pipes
 * used are `translate` + `parse` (from LegacyShimsModule) and `lowercase` /
 * `number` (CommonModule).
 *
 * STANDALONE (deep-path imports CommonModule + LegacyShimsModule — never the
 * account-shared barrel) so it can be reused by BOTH AdvocacyGroupModule (which
 * imports it instead of declaring it) AND the platform layer leaves (post-stay /
 * pre-stay campaigns sections) without re-declaration. Proven-safe pattern — same
 * as the platform engagement-story components.
 *
 * Faithful-port notes:
 * - Root `[ngClass]` uses the FIRST of v1's two duplicate `ng-class` attrs
 *   (HTML5 keeps the first): the `disabled` / `other` / `custom` ternary.
 * - `ft-sortable` / `ft-sort-up` / `ft-sort-down` collapse into `moveUp` /
 *   `moveDown` click handlers that reorder `campaigns` IN PLACE (the journeys
 *   "Change order" link reads that mutated array in `saveSortOrder`) and emit
 *   `campaignSortChanged` (mirrors v1's `onCampaignSortChanged`, unbound today).
 * - `share` / `percentage` math is inlined faithfully (no Math.ceil — unlike the
 *   shared PipesModule pipes), formatted via the CommonModule `number` pipe.
 *
 * Renders inside `ft-state` `state-inactive` panels at QA-login (display:none),
 * so it never appears at gate time; this full port serves real customers with
 * active campaigns (the prior stub rendered '').
 */
@Component({
    selector: 'ft-campaign-list',
    imports: [CommonModule, LegacyShimsModule],
    templateUrl: './campaign-list.component.html'
})
export class FtCampaignListComponent {
    readonly campaigns = input<any[]>([]);
    readonly sortMode = input<any>();
    readonly layerType = input<string | number>();
    readonly campaignSelected = output<{ id: string }>();
    readonly campaignSortChanged = output<{ campaignIds: string[] }>();

    /** criteria key -> translation key (only these 5 produced text in v1). */
    private static readonly CRITERIA_LABELS: Record<string, string> = {
        roomTypes: 'flipto.account.platform.preStay.campaign.RoomType',
        dateRanges: 'flipto.account.platform.preStay.campaign.DateRange',
        stayLengths: 'flipto.account.platform.preStay.campaign.LengthOfStay',
        languages: 'flipto.account.platform.preStay.campaign.Languages',
        rateCodes: 'flipto.account.platform.preStay.campaign.RateCode',
    };

    /** v1 row click selects unless in sort mode. */
    protected selectCampaign(campaign: any): void {
        if (this.sortMode()) return;
        this.campaignSelected.emit({ id: campaign.id });
    }

    /** v1's FIRST `ng-class` ternary (the duplicate-attribute winner). */
    protected rootClass(campaign: any): string {
        if (!this.parseBool(campaign?.isActive)) return 'disabled';
        return campaign?.type == 255 ? 'other' : 'custom';
    }

    /** v1 `campaignName` filter: returns a translation KEY for the two special
     *  cases (resolved by `| translate` in the template), or null to use
     *  `campaign.name`. */
    protected campaignNameKey(campaign: any): string | null {
        if (!campaign) return null;
        if (this.layerType() == 6 && campaign.type == 255) return 'flipto.components.campaign.AllVisitors';
        if (campaign.type == 255) return 'flipto.components.campaign.AllOtherGuests';
        return null;
    }

    /** Matched-criteria translation keys, in object-key order (v1 rendered the
     *  5 known keys translated + lowercased, comma-joined). */
    protected criteriaKeys(campaign: any): string[] {
        const criteria = campaign?.criteria;
        if (!criteria) return [];
        return Object.keys(criteria)
            .filter((k) => !!FtCampaignListComponent.CRITERIA_LABELS[k])
            .map((k) => FtCampaignListComponent.CRITERIA_LABELS[k]);
    }

    /** v1 `arrayFilterByPropertyInArray:'type':[1,3,4,5,6,7]` (advocate goals). */
    protected advocateGoals(campaign: any): any[] {
        return this.goalsByType(campaign, [1, 3, 4, 5, 6, 7]);
    }

    /** v1 `arrayFilterByPropertyInArray:'type':[2]` (warm-lead / social goals). */
    protected warmLeadGoals(campaign: any): any[] {
        return this.goalsByType(campaign, [2]);
    }

    private goalsByType(campaign: any, types: number[]): any[] {
        const items = campaign?.goals?.item;
        if (!items) return [];
        return items.filter((g: any) => types.some((t) => g.type == t));
    }

    /** v1 `capitalize:true` (isSentence): keep casing, upper-case first char only. */
    protected capitalize(source: any, isSentence = false): string {
        if (source === null || source === undefined || source === '') return '';
        const text = String(source);
        const lower = isSentence ? text : text.toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    }

    /** v1 `percentage`: intOrDefault(value)/intOrDefault(total)*100, NaN->0. */
    protected engagementPercent(campaign: any): number {
        const pct = (this.intOrDefault(campaign?.campaignShares) / this.intOrDefault(campaign?.campaignGuests)) * 100;
        return Number.isNaN(pct) ? 0 : pct;
    }

    /** v1 `share`: intOrDefault(value)/intOrDefault(total), NaN->0. */
    protected visitorsPerAdvocate(campaign: any): number {
        const s = this.intOrDefault(campaign?.campaignUniqueVisitors) / this.intOrDefault(campaign?.campaignShares);
        return Number.isNaN(s) ? 0 : s;
    }

    protected canBeMovedDown(campaign: any): boolean {
        const i = this.campaigns()?.indexOf(campaign) ?? -1;
        const next = this.campaigns()?.[i + 1];
        return !!next && this.parseBool(next.isActive) && next.type !== 255;
    }

    protected moveUp(campaign: any, event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.move(campaign, -1);
    }

    protected moveDown(campaign: any, event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.move(campaign, 1);
    }

    /** Reorder `campaigns` in place (parent reads the mutated array on save). */
    private move(campaign: any, delta: number): void {
        if (!this.campaigns()) return;
        const from = this.campaigns().indexOf(campaign);
        const to = from + delta;
        if (from < 0 || to < 0 || to >= this.campaigns().length) return;
        this.campaigns().splice(to, 0, this.campaigns().splice(from, 1)[0]);
        const campaignIds = this.campaigns()
            .filter((c) => this.parseBool(c.isActive) && c.type !== 255)
            .map((c) => c.id);
        this.campaignSortChanged.emit({ campaignIds });
    }

    /** v1 parse.intOrDefault: parseInt(x,10) or 0. */
    private intOrDefault(source: any): number {
        const n = parseInt(source, 10);
        return Number.isNaN(n) ? 0 : n;
    }

    /** v1 parse.bool: true only for true / '1' / 'true'. */
    private parseBool(source: any): boolean {
        return source === true || source == '1' || source === 'true';
    }
}
